import React from "react";

import { formatCurrency } from "@eave/shared/utils/currency";

import SimpleTable from "./SimpleTable";

const styles = require("./styles.module.scss");

interface Props {
  dueFromBorrower: DueFromBorrower;
  totalMortgageLoans: TotalMortgageLoans;
  totalCredits: TotalCredits;
}
export interface DueFromBorrower {
  salesContractPrice: number;
  improvementsRenovationsRepairs: number;
  land: number;
  mortgagePayoff: number;
  creditCardsOther: number;
  borrowerClosingCosts: number;
  discountPoints: number;
}

interface TotalMortgageLoans {
  loanAmount: LoanAmount;
  otherLoans: number;
}
interface LoanAmount {
  amount: number;
  withoutInsurance: number;
  insurance: number;
}

interface TotalCredits {
  sellerCredits: number;
  otherCredits: number;
}

const Summary = ({
  dueFromBorrower: {
    salesContractPrice,
    improvementsRenovationsRepairs,
    land,
    mortgagePayoff,
    creditCardsOther,
    borrowerClosingCosts,
    discountPoints,
  },
  totalMortgageLoans: {
    loanAmount: { amount, withoutInsurance, insurance },
    otherLoans,
  },
  totalCredits: { sellerCredits, otherCredits },
}: Props) => {
  const totalDueFromBorrowers =
    salesContractPrice +
    improvementsRenovationsRepairs +
    land +
    mortgagePayoff +
    creditCardsOther +
    borrowerClosingCosts +
    discountPoints;

  const totalMortgageLoans = amount + otherLoans;

  const totalCredits = sellerCredits + otherCredits;

  const dueFromBorrowerRowData = [
    {
      id: "a",
      name: "A. Sales Contract Price",
      value: salesContractPrice,
    },
    {
      id: "b",
      name: "B. Improvements, Renovations, and Repairs",
      value: improvementsRenovationsRepairs,
    },
    {
      id: "c",
      name: (
        <p>
          C. Land <em>(if acquired separately)</em>
        </p>
      ),
      value: land,
    },
    {
      id: "d",
      name: "D. For Refinance: Balance of Mortgage Loans on the Property to be paid off in the Transaction ",
      value: mortgagePayoff,
    },
    {
      id: "e",
      name: "E. Credit Cards and Other Debts Paid Off",
      value: creditCardsOther,
    },
    {
      id: "f",
      name: (
        <p>
          F. Borrower Closing Costs <em>(including Prepaid and Initial Escrow Payments)</em>
        </p>
      ),
      value: borrowerClosingCosts,
    },
    {
      id: "g",
      name: "G. Discount Points",
      value: discountPoints,
    },
    {
      id: "h",
      name: (
        <p>
          <strong>H. TOTAL DUE FROM BORROWER(S)</strong> <em>(Total of A through G)</em>
        </p>
      ),
      value: totalDueFromBorrowers,
    },
  ];

  const totalMortgageLoansRowData = [
    {
      id: "i",
      name: (
        <p>
          <p className={`${styles.verticalTop} ${styles.inlineBlock}`}>I.</p>{" "}
          <div className={styles.loanAmountInner}>
            <p>Loan Amount</p>
            <div className={styles.loanAmountBox}>
              <p>
                Loan Amount Excluding Financed Mortgage Insurance{" "}
                <em>(or Mortgage Insurance Equivalent)</em>
              </p>
              <p>{formatCurrency(withoutInsurance)}</p>
            </div>
            <div className={styles.loanAmountBox}>
              <p>
                Financed Mortgage Insurance <em>(or Mortgage Insurance Equivalent)</em>
              </p>
              <p>{formatCurrency(insurance)}</p>
            </div>
          </div>
        </p>
      ),
      value: amount,
      valueStyle: styles.verticalTop,
    },
    {
      id: "j",
      name: "J. Other New Mortgage Loans on the Property the Borrower(s) is Buying or Refinancing",
      value: otherLoans,
    },
    {
      id: "k",
      name: (
        <p>
          <strong>K. TOTAL MORTGAGE LOANS</strong> (Total of I and J)
        </p>
      ),
      value: totalMortgageLoans,
    },
  ];

  const totalCreditsRowData = [
    {
      id: "l",
      name: (
        <p>
          L. Seller Credits <em>(Borrower(s) costs paid by the property seller)</em>
        </p>
      ),
      value: sellerCredits,
    },
    {
      id: "m",
      name: (
        <p>
          M. Other Credits{" "}
          <em>
            (Sum of all other credits - Borrower Paid Fees, Earnest Money, Employer Assisted
            Housing, Lease Purchase Fun, Lot Equity, Relocation Funds, Sweat Equity, Trace Equity,
            Other)
          </em>
        </p>
      ),
      value: otherCredits,
    },
    {
      id: "n",
      name: (
        <p>
          <strong>N. TOTAL CREDITS</strong> (Total of L and M)
        </p>
      ),
      value: totalCredits,
    },
  ];

  const calculationRowData = [
    {
      id: "totalFromBorrowers",
      name: (
        <p>
          TOTAL DUE FROM BORROWER(S) <em>(Line H)</em>
        </p>
      ),
      value: totalDueFromBorrowers,
    },
    {
      id: "totalMortgageLoansAndCredits",
      name: (
        <p>
          LESS TOTAL MORTGAGE LOANS <em>(Line K)</em> AND TOTAL CREDITS <em>(Line N)</em>
        </p>
      ),
      value: totalMortgageLoans + totalCredits,
    },
    {
      id: "cashFromToBorrower",
      name: (
        <p>
          <strong>Cash From/To the Borrower (Line H minus Line K and Line N)*</strong>
          <br />
          *NOTE: This amount does not include reserves or other funds that may be required by the
          Lender to be verified.
        </p>
      ),
      value: totalDueFromBorrowers - totalMortgageLoans - totalCredits,
    },
  ];

  return (
    <>
      <SimpleTable title="Due From Borrower(s)" rowData={dueFromBorrowerRowData} />
      <SimpleTable title="Total Mortgage Loans" rowData={totalMortgageLoansRowData} />
      <SimpleTable title="Total Credits" rowData={totalCreditsRowData} />
      <SimpleTable title="Calculation" rowData={calculationRowData} />
    </>
  );
};

export default Summary;
